import CommonIcons from "@base/assets/icons";
import CommonImages from "@base/assets/images";
import { getAvatarLink, locateToLocationOnGGMap } from "@base/helpers/common";
import useGetSiteBySlug from "@base/modules/site/hooks/useGetSiteBySlug";
import { ISite } from "@base/modules/site/site.interface";
import { responseUI } from "@base/themes/response";
import CommonStyles from "@components/CommonStyles";
import TabDoctor from "@components/OrderFacility/[id]/TabDoctor";
import TabInfo from "@components/OrderFacility/[id]/TabInfo";
import TabPackageService from "@components/OrderFacility/[id]/TabPackageService";
import { TabsProps } from "antd";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useParams } from "react-router-dom";
import useSiteBooking, { isSiteBookingHidden } from "@base/hooks/useSiteBooking";

export default function OrderFacilityDetail() {
  //! state
  const { t } = useTranslation();
  const { slug: site_slug } = useParams();
  const [activeKey, setActiveKey] = useState<string>("1");
  const { data: dataSite } = useGetSiteBySlug(String(site_slug));
  const site = dataSite?.data as ISite;

  const tabs: TabsProps["items"] = [
    {
      key: "1",
      label: t("Information"),
      children: <TabInfo site={site} />,
    },
    {
      key: "2",
      label: t("Doctors"),
      children: <TabDoctor site={site} />,
    },
    {
      key: "3",
      label: t("ServicePackages"),
      children: <TabPackageService site={site} />,
    },
  ];

  //! function
  const { start: startBooking, isStarting, branchPicker } = useSiteBooking();

  //! render
  return (
    <div className="bg-common-white">
      <div className={`bg-common-white  pb-8 relative  lg:py-5 flex flex-col justify-between ${responseUI.screen}`}>
        <div
          className="h-[200px] lg:h-[368px] relative"
          style={{
            backgroundImage: `url(${getAvatarLink(site?.avatar ?? [], "banner")}), url(${CommonImages.BannerDefaultSite}) `,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
            objectFit: "cover",
          }}
        >
          <div className="lg:hidden bg-common-background p-3 rounded-2xl absolute -bottom-10 left-1/2 -translate-x-1/2">
            <div className="w-[50px]">
              <img
                className="aspect-square object-contain"
                src={getAvatarLink(site?.avatar ?? [])}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null;
                  currentTarget.src = CommonImages.SiteDefault;
                }}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap lg:flex-nowrap gap-7 p-4  bg-common-white mt-10 lg:mt-0">
          <div className="hidden basis-1/3 lg:basis-1/4 lg:flex bg-common-background p-3 rounded-2xl w-full">
            <div className="flex justify-center items-center ">
              <CommonStyles.ImageUI
                className="lg:aspect-square xl:w-[237px] object-contain"
                src={getAvatarLink(site?.avatar ?? [])}
                fallback={CommonImages.SiteDefault}
              />
            </div>
          </div>

          <div className="lg:basis-3/4">
            <div className="flex flex-col justify-center h-full gap-6">
              <CommonStyles.TypographyUI use="title" level={1} className="!font-medium lg:!text-xl !text-common-maintext !m-0">
                {site?.name}
              </CommonStyles.TypographyUI>

              <div className="flex flex-col gap-3">
                {site?.address ? (
                  <div className="flex items-center lg:gap-3">
                    <div>
                      <CommonIcons.IconLocation />
                    </div>
                    <CommonStyles.TypographyUI className="lg:text-base text-common-subtext">{site.address}</CommonStyles.TypographyUI>
                  </div>
                ) : null}

                <div className="flex items-center lg:gap-3">
                  <div>
                    <CommonIcons.IconNetWork />
                  </div>
                  <NavLink className="text-common-inprogress underline" to={String(site?.description_link)}>
                    {site?.description_link}
                  </NavLink>
                </div>
              </div>

              <div className="flex items-center justify-center lg:justify-start gap-4">
                {!isSiteBookingHidden(site) ? (
                  <CommonStyles.ButtonUI onClick={() => startBooking(site)} loading={isStarting} className="lg:h-10 font-medium w-32">
                    {t("home.bookAppointment")}
                  </CommonStyles.ButtonUI>
                ) : null}
                <CommonStyles.ButtonUI
                  onClick={() => (site.lon && site.lat ? locateToLocationOnGGMap({ lon: site.lon, lat: site.lat }) : {})}
                  className="lg:h-10 font-medium bg-common-primary100 text-common-mainColor hover:!bg-common-primary200 hover:!text-common-mainColor w-32"
                >
                  {t("Direct")}
                </CommonStyles.ButtonUI>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-common-background">
        <div className={`bg-common-background py-4 ${responseUI.screen}`}>
          <CommonStyles.TabsUI
            activeKey={activeKey}
            onChange={(value) => {}}
            items={tabs}
            renderTabBar={(props) => <CustomTabBar tabs={tabs} setActiveKey={setActiveKey} {...props} />}
          />
        </div>
      </div>

      {branchPicker}
    </div>
  );
}

const CustomTabBar = ({
  activeKey,
  setActiveKey,
  tabs,
}: {
  activeKey: string;
  setActiveKey: Dispatch<SetStateAction<string>>;
  tabs: TabsProps["items"];
}) => {
  //! state

  //! function
  const handleTabClick = (key: any) => {
    setActiveKey(key);
  };

  //! render
  const renderTab = () => {
    return tabs?.map((item, index) => {
      const active = activeKey === `${item.key}` ? "text-common-primary bg-common-primary100" : "";
      return (
        <div
          className={`py-3 lg:px-10 text-common-maintext font-medium text-xs lg:text-base text-center lg:gap-3 rounded-lg cursor-pointer grow custom-tab  ${active}`}
          onClick={() => handleTabClick(item.key)}
          key={index}
        >
          {item.icon}
          {item.label}
        </div>
      );
    });
  };
  return <div className="custom-tab-bar bg-white p-3 rounded-2xl flex justify-between">{renderTab()}</div>;
};
